import { useState, useEffect } from 'react';
import Alert, { type AlertOptions, ALERT_EVENT } from './Alert';

interface AlertContainerProps {
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
    maxAlerts?: number;
    className?: string;
}

interface AlertWithId extends AlertOptions {
    id: string;
}

const positionToClass = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
    'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2'
};

const AlertContainer = ({ position = 'top-right', maxAlerts = 5, className = '' }: AlertContainerProps) => {
    const [alerts, setAlerts] = useState<AlertWithId[]>([]);

    useEffect(() => {
        const handleAlertEvent = (event: Event) => {
            const customEvent = event as CustomEvent<{ type: string; alert?: AlertOptions; id?: string }>;
            const { type, alert, id } = customEvent.detail;

            if (type === 'add' && alert) {
                setAlerts((prevAlerts) => {
                    // Create a new array of alerts, removing the oldest if we exceed maxAlerts
                    const newAlerts = [...prevAlerts];

                    if (newAlerts.length >= maxAlerts) {
                        newAlerts.shift(); // Remove the oldest alert
                    }

                    return [...newAlerts, { ...alert, id: alert.id || `alert-${Date.now()}` }];
                });
            } else if (type === 'remove' && id) {
                setAlerts((prevAlerts) => prevAlerts.filter((a) => a.id !== id));
            }
        };

        // Add event listener for alert events
        document.addEventListener(ALERT_EVENT, handleAlertEvent);

        // Clean up
        return () => {
            document.removeEventListener(ALERT_EVENT, handleAlertEvent);
        };
    }, [maxAlerts]);

    if (alerts.length === 0) {
        return null;
    }

    return (
        <div className={`fixed p-3 ${positionToClass[position]} ${className}`} style={{ zIndex: 9999, maxWidth: '350px' }}>
            {alerts.map((alert) => (
                <Alert
                    autoHide={alert.autoHide}
                    autoHideDelay={alert.autoHideDelay}
                    className="mb-2"
                    dismissible={alert.dismissible}
                    heading={alert.heading}
                    icon={alert.icon}
                    key={alert.id}
                    variant={alert.variant}
                    onClose={() => {
                        setAlerts((prevAlerts) => prevAlerts.filter((a) => a.id !== alert.id));
                    }}
                >
                    {alert.message}
                </Alert>
            ))}
        </div>
    );
};

export default AlertContainer;
